import { Directive, Input, OnChanges, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionExpressionService } from '@shared/services/permission-expression.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { Subject, takeUntil } from 'rxjs';

// Declarative alternative to each component computing its own hasPermissions() flag and binding a
// *ngIf to it - gates the host element directly from the template, so adding permission-gating to
// a new element never requires touching the component's .ts file.
@Directive({
	selector: '[appHasPermission]',
	standalone: false,
})
export class HasPermissionDirective implements OnChanges, OnDestroy {
	@Input() appHasPermission: string | string[];

	private readonly destroy$ = new Subject<void>();

	constructor(
		private templateRef: TemplateRef<unknown>,
		private viewContainer: ViewContainerRef,
		private userPermissionsService: UserPermissionsService,
		private permissionExpressionService: PermissionExpressionService,
	) {
		// Re-evaluates whenever the session's permission codes actually change (e.g. account switch,
		// permissions refreshed post-login) - not just when this directive's own input changes, so
		// gated elements don't stay stuck on a stale allowed/denied decision.
		this.userPermissionsService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateView());
	}

	ngOnChanges(): void {
		this.updateView();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateView(): void {
		this.viewContainer.clear();
		if (this.permissionExpressionService.hasPermission(this.appHasPermission)) {
			this.viewContainer.createEmbeddedView(this.templateRef);
		}
	}
}
